import { FunctionComponent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { BeehiveReviewModal, QuestStatusBadge } from 'src/shared';
import { ClientQuest, QuestStatusMap } from 'src/types/client';
import { QuestStatus } from 'src/enums/quests';
import { PlanningSessionStatus } from 'src/types/planning';
import { CellLabel, ClickableBadgeWrapper, TooltipAnchor } from './styled';
import Button from 'src/shared/Button';
import color from 'src/theme/color';
import { ReactComponent as ArrowRightIcon } from 'src/assets/icons/right_arrow.svg';
import { RoutesEnum } from 'src/types/navigation';
import { ArrowButton, StatusText, CircleButton } from 'src/pages/Client/styled';
import theme from 'src/theme';
import ReviewSplitButton from 'src/shared/ReviewSplitButton';
import { getQuestStatusColors } from 'src/utils/questStatusColors';

interface Props {
    clientQuest: ClientQuest;
    onStatusChange: (status: QuestStatus) => void;
    onRefresh: () => void;
}

const QuestStatusCell: FunctionComponent<Props> = ({
    clientQuest,
    onStatusChange,
    onRefresh
}) => {
    const navigate = useNavigate();
    const { quest, planningStatus } = clientQuest;
    const [beehiveModalOpen, setBeehiveModalOpen] = useState(false);

    const handleBeehiveReview = useCallback(() => {
        setBeehiveModalOpen(true);
    }, []);

    const handleTitleClick = useCallback(() => {
        if (quest.status === QuestStatus.DRAFT) {
            navigate(`${RoutesEnum.PLAN_QUEST}?questId=${quest.id}`);
        } else {
            navigate(RoutesEnum.QUEST_DETAIL.replace(':id', quest.id));
        }
    }, [navigate, quest.id, quest.status]);

    const handlePlanningClick = useCallback(() => {
        if (clientQuest.planningSessionId) {
            navigate(
                RoutesEnum.PLAN_QUEST_SESSION.replace(
                    ':sessionId',
                    clientQuest.planningSessionId
                )
            );
        }
    }, [navigate, clientQuest.planningSessionId]);

    const renderStatusBadge = () => {
        if (quest.status === QuestStatus.IN_PROCESS) {
            return (
                <>
                    <QuestStatusBadge
                        text={QuestStatusMap[quest.status]}
                        {...getQuestStatusColors(quest.status)}
                    />
                    {quest.iterations > 1 && (
                        <CellLabel>{`Iteration ${quest.iterations}`}</CellLabel>
                    )}
                </>
            );
        }

        if (quest.status === QuestStatus.IN_REVIEW) {
            return (
                <ReviewSplitButton
                    onReviewRequest={() =>
                        navigate(
                            RoutesEnum.QUEST_REVIEW.replace(':id', quest.id)
                        )
                    }
                    onBeehiveReview={handleBeehiveReview}
                >
                    <ClickableBadgeWrapper role="button" tabIndex={0}>
                        <QuestStatusBadge
                            text="Review"
                            color={theme.color.white}
                            backgroundColor={color.lightningYellow}
                        />
                    </ClickableBadgeWrapper>
                </ReviewSplitButton>
            );
        }

        if (quest.status === QuestStatus.ON_HOLD) {
            return (
                <Tooltip title="Start development" placement="top" arrow>
                    <TooltipAnchor>
                        <ArrowButton>
                            <StatusText>
                                {QuestStatusMap[quest.status]}
                            </StatusText>
                            <CircleButton>
                                <ArrowRightIcon
                                    onClick={() =>
                                        onStatusChange(QuestStatus.NEW)
                                    }
                                />
                            </CircleButton>
                        </ArrowButton>
                    </TooltipAnchor>
                </Tooltip>
            );
        }

        if (quest.status === QuestStatus.PLANNING) {
            if (planningStatus === PlanningSessionStatus.AWAITING_ANSWERS) {
                return (
                    <Button
                        onClick={handlePlanningClick}
                        color={color.lightningYellow}
                        styles={{ minWidth: '100px', width: 'auto' }}
                        borderRadius="40px"
                    >
                        Awaiting Answers
                    </Button>
                );
            }
            if (planningStatus === PlanningSessionStatus.PLAN_READY) {
                return (
                    <Button
                        onClick={handlePlanningClick}
                        color={color.lightningYellow}
                        styles={{ minWidth: '100px', width: 'auto' }}
                        borderRadius="40px"
                    >
                        Review Plan
                    </Button>
                );
            }
            if (planningStatus === PlanningSessionStatus.FAILED) {
                return (
                    <QuestStatusBadge
                        text="Planning Failed"
                        color={theme.color.red}
                        backgroundColor={theme.color.darkMintRed}
                    />
                );
            }
            return (
                <QuestStatusBadge
                    text="Planning Processing"
                    {...getQuestStatusColors(quest.status)}
                />
            );
        }

        if (quest.status === QuestStatus.DONE && quest.isMerged) {
            return (
                <QuestStatusBadge
                    text="Merged"
                    color={theme.color.brightGreen}
                    backgroundColor={theme.color.darkGreenBlack}
                />
            );
        }

        if (quest.status === QuestStatus.DRAFT) {
            return (
                <span
                    role="button"
                    tabIndex={0}
                    onClick={handleTitleClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTitleClick();
                        }
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <QuestStatusBadge
                        text={QuestStatusMap[quest.status]}
                        {...getQuestStatusColors(quest.status)}
                    />
                </span>
            );
        }

        return (
            <QuestStatusBadge
                text={QuestStatusMap[quest.status]}
                {...getQuestStatusColors(quest.status)}
            />
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '6px'
            }}
        >
            {renderStatusBadge()}
            <BeehiveReviewModal
                open={beehiveModalOpen}
                questId={quest.id}
                onClose={() => setBeehiveModalOpen(false)}
                onSuccess={onRefresh}
            />
        </div>
    );
};

export default QuestStatusCell;
